import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "ui-ux",
  name: "UI/UXデザイン",
  nameEn: "UI/UX Design",
  seo: {
    title: "UI/UXデザインを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "UI/UXデザインはユーザーに愛されるプロダクトを生み出す技術です。ゆめさくエンジニアならFigma・ユーザビリティ・デザインシステムを実践的に学べます。",
    url: "/columns/ui-ux-programming-school",
  },
  hero: {
    title: "UI/UXデザインを学べる女性向けプログラミングスクール",
    englishTitle: "Learn UI/UX Design",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "UI/UXデザイン" },
  ],
  overview: {
    heading: "UI/UXデザインとは？",
    paragraphs: [
      "UI（User Interface）デザインは、ボタンの配置・色・タイポグラフィなど、ユーザーが直接触れる画面の見た目を設計する技術です。UX（User Experience）デザインは、ユーザーがプロダクトを使う過程全体の体験を設計します。",
      "美しいだけでなく「使いやすい」プロダクトを作るには、UIとUXの両方の知識が不可欠です。ユーザーリサーチ、ワイヤーフレーム作成、プロトタイピング、ユーザビリティテストなどの工程を通じて、人々に愛されるプロダクトを生み出します。",
      "Apple、Airbnb、Notionなど、成功しているプロダクトは例外なくUI/UXデザインに力を入れています。テクノロジー企業だけでなく、あらゆる業界でUI/UXの重要性が認識されています。",
    ],
    useCases: [
      {
        label: "Webサイト・アプリのデザイン",
        description:
          "ユーザーが迷わず目的を達成できるナビゲーション設計やレイアウト、インタラクションを設計します。",
      },
      {
        label: "デザインシステム構築",
        description:
          "カラー、タイポグラフィ、コンポーネントをルール化し、チーム全体で一貫したデザインを実現する仕組みを作ります。",
      },
      {
        label: "ユーザーリサーチ",
        description:
          "インタビュー、アンケート、行動観察などを通じてユーザーの課題やニーズを明らかにし、デザインの方向性を決定します。",
      },
      {
        label: "アクセシビリティ対応",
        description:
          "障害のある方や高齢者を含む、すべてのユーザーが使えるプロダクトを設計します。WCAG基準に準拠したデザインを行います。",
      },
    ],
  },
  whyLearn: {
    heading: "UI/UXデザインを学ぶべき3つの理由",
    items: [
      {
        title: "テクノロジー × デザインの掛け算で希少価値が高い",
        description:
          "プログラミングスキルとデザインスキルの両方を持つ人材は非常に希少です。ゆめさくエンジニアでコーディングの基礎を学びながらUI/UXスキルも身につけることで、市場価値の高いエンジニアを目指せます。",
      },
      {
        title: "ユーザー視点の思考力が身につく",
        description:
          "UI/UXデザインを学ぶことで「作る側」だけでなく「使う側」の視点で考える力が養われます。この思考力はエンジニアリング、マーケティング、プロダクトマネジメントなど幅広い職種で活きます。",
      },
      {
        title: "リモートワーク・フリーランスとの相性が良い",
        description:
          "UI/UXデザインはパソコン一台で完結する仕事が多く、場所を選ばない働き方が実現しやすい分野です。副業としてデザイン案件を受けることもでき、働き方の選択肢が広がります。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでUI/UXデザインを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアではPhase 3の応用〜上級としてUI/UXデザインを学びます。HTMLやCSS、Reactの知識を持った状態でデザインに入るため、「実装できるデザイナー」としての視点を自然に身につけることができます。",
      "Figmaを使ったワイヤーフレーム・プロトタイプ制作から、デザインシステム構築、ユーザビリティテストまで実践的に学び、ポートフォリオに載せられる成果物を制作します。",
    ],
    topics: [
      "デザインの基本原則（近接・整列・反復・コントラスト）",
      "Figmaの操作・コンポーネント設計",
      "ワイヤーフレーム・プロトタイプ制作",
      "カラー理論・タイポグラフィ",
      "ユーザビリティテストの設計と実施",
      "アクセシビリティ（WCAG準拠のデザイン）",
    ],
  },
  careerPaths: {
    heading: "UI/UXデザインを活かせるキャリア",
    items: [
      {
        title: "UIデザイナー",
        description:
          "WebサイトやアプリのUI設計を専門に行います。Figmaなどのツールを使い、ビジュアルデザインからインタラクション設計まで一貫して担当します。",
        salary: "400〜650万円",
      },
      {
        title: "UXデザイナー",
        description:
          "ユーザーリサーチや情報設計を通じて、プロダクト全体のユーザー体験を最適化します。プロダクトマネージャーやエンジニアと密に連携し、事業成長に貢献します。",
        salary: "500〜800万円",
      },
      {
        title: "フロントエンドエンジニア（デザイン寄り）",
        description:
          "デザインスキルとコーディングスキルの両方を活かし、高品質なUIの実装を担当します。デザインシステムの構築・運用やアニメーション実装など、デザインとエンジニアリングの橋渡し役を担います。",
        salary: "500〜800万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "6〜12ヶ月（ツールの使い方で終わりがち）",
        luagate: "3〜4ヶ月（設計原則からツール活用まで体系的に）",
      },
      {
        label: "挫折率",
        selfStudy: "感覚的になりフィードバックが得られず停滞",
        luagate: "プロのデザインレビューで着実に成長",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "模写ばかりでオリジナル作品が作れない",
        luagate: "実際のプロダクトを想定したデザインプロジェクト",
      },
      {
        label: "質問対応",
        selfStudy: "デザインの良し悪しを自分で判断できない",
        luagate: "現役デザイナーが根拠を明示してフィードバック",
      },
      {
        label: "就職サポート",
        selfStudy: "すべて自力で進める",
        luagate: "ポートフォリオ添削・面接対策まで一貫サポート",
      },
    ],
  },
  faq: [
    {
      question: "デザインセンスがなくても学べますか？",
      answer:
        "はい、UI/UXデザインはセンスよりも「原則」と「論理」に基づく技術です。近接・整列・反復・コントラストなどの基本原則を学べば、誰でも論理的に美しいデザインを作ることができます。ゆめさくエンジニアでは感覚ではなく理論からしっかり学びます。",
    },
    {
      question: "Figmaは無料で使えますか？",
      answer:
        "はい、Figmaには無料プランがあり、個人学習には十分な機能が揃っています。ブラウザ上で動作するためインストール不要で、ゆめさくエンジニアの学習でもすぐに始められます。",
    },
    {
      question: "UI/UXデザインとWebデザインの違いは何ですか？",
      answer:
        "Webデザインは主にWebサイトの見た目を作ることを指しますが、UI/UXデザインはより広い概念です。ユーザーリサーチ、情報設計、プロトタイピング、ユーザビリティテストなど、プロダクト全体の体験設計を含みます。",
    },
    {
      question: "エンジニアがUI/UXを学ぶメリットは何ですか？",
      answer:
        "デザイナーとの共通言語が生まれ、チーム内のコミュニケーションが円滑になります。また、自分でデザインから実装まで一貫して行える「フルスタック型」の人材は市場価値が高く、フリーランスや副業でも有利です。",
    },
    {
      question: "UI/UXデザイナーの需要は今後も増えますか？",
      answer:
        "デジタルプロダクトが増え続ける限り、UI/UXデザイナーの需要は拡大し続けます。特にDX推進やノーコードツールの普及により、デザインスキルを持つ人材の活躍の場はますます広がっています。",
    },
  ],
  relatedLanguages: [
    { slug: "html-css", name: "HTML/CSS" },
    { slug: "react", name: "React" },
    { slug: "component-design", name: "コンポーネント設計" },
    { slug: "javascript", name: "JavaScript" },
  ],
};
